import { useGetApiUsersQuery } from "@shared/index";
import { useEffect, useRef, useState } from "react";
import { REACTIONS_TYPES } from "@shared/constants";
import { ApplicationUserDto } from "@entities/commonTypes";

interface ReactionsPaginationResponse {
  data: ApplicationUserDto[];
  meta?: {
    page: number;
    limit: number;
    counter: number;
  };
}

export const useUserRatingPagination = (reactionsType: REACTIONS_TYPES) => {
  const [reactions, setReactions] = useState<ReactionsPaginationResponse>();
  const [page, setPage] = useState(1);
  const [limit] = useState(7);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const listEndRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isTriggeringRef = useRef(false);

  const { data, error, isError, isLoading, isFetching } = useGetApiUsersQuery(
    {
      page,
      limit,
      sorting: "by_social_rating",
    },
    {
      skip: reactionsType !== REACTIONS_TYPES.UserRating,
    }
  );

  const processData = (apiData: any, isInitial = false) => {
    if (isInitial) {
      setReactions(apiData);
    } else {
      setReactions((prevData: ReactionsPaginationResponse | undefined) => {
        const existingIds = new Set(
          (prevData?.data || []).map((user) => user.id)
        );
        const newUsers = (apiData.data || []).filter(
          (user: ApplicationUserDto) => !existingIds.has(user.id)
        );
        const newData: ReactionsPaginationResponse = {
          meta: apiData.meta || prevData?.meta,
          data: [...(prevData?.data || []), ...newUsers],
        };
        return newData;
      });
    }

    if (!apiData.data || apiData.data.length === 0) {
      setHasMoreData(false);
    } else if (apiData.data.length < limit) {
      setHasMoreData(false);
    }
  };

  const initializeReactions = () => {
    if (observerRef.current && listEndRef.current) {
      observerRef.current.unobserve(listEndRef.current);
      observerRef.current = null;
    }
    setPage(1);
    setHasMoreData(true);
    setReactions(undefined);
    setIsInitialLoad(true);
  };

  useEffect(() => {
    initializeReactions();
  }, [reactionsType]);

  useEffect(() => {
    if (error && isError) {
      console.error("Error fetching data:", error);
      setHasMoreData(false);
      setIsInitialLoad(false);
      return;
    }

    if (data) {
      if (page === 1) {
        processData(data, true);
      } else {
        processData(data, false);
      }
      setIsInitialLoad(false);
    }
  }, [data, error, isError, page]);

  useEffect(() => {
    if (!reactions || !hasMoreData || isLoading) {
      return;
    }

    if (observerRef.current && listEndRef.current) {
      observerRef.current.unobserve(listEndRef.current);
      observerRef.current = null;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !isLoading &&
          hasMoreData &&
          !isTriggeringRef.current
        ) {
          isTriggeringRef.current = true;
          setPage((prevPage) => prevPage + 1);
        }
      },
      {
        root: null,
        rootMargin: "20px",
        threshold: 0.1,
      }
    );

    if (listEndRef.current) {
      observerRef.current.observe(listEndRef.current);
    }

    return () => {
      if (observerRef.current && listEndRef.current) {
        observerRef.current.unobserve(listEndRef.current);
      }
    };
  }, [isLoading, reactions, hasMoreData]);

  useEffect(() => {
    if (!isLoading && data) {
      isTriggeringRef.current = false;
    }
  }, [isLoading, data]);

  return {
    page,
    limit,
    listEndRef,
    reactions,
    loading: isLoading || isFetching,
    hasMoreData,
    error,
    isError,
    isInitialLoad,
  };
};
